import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'
import vuetify from '@/plugins/vuetify'

export type ThemePreference = 'light' | 'dark' | 'system'

const PREFERENCE_KEY = 'theme_preference'

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null // blocked storage (e.g. private-mode Safari)
  }
}
function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* degrade silently when storage is blocked */
  }
}

export const useThemeStore = defineStore('theme', () => {
  const storedPreference = readStorage(PREFERENCE_KEY)
  const preference = ref<ThemePreference>(
    storedPreference === 'light' || storedPreference === 'dark' || storedPreference === 'system'
      ? storedPreference
      : 'system'
  )
  const systemDark = ref(false)

  if (typeof window.matchMedia === 'function') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemDark.value = mediaQuery.matches
    const onChange = (event: MediaQueryListEvent) => {
      systemDark.value = event.matches
    }
    mediaQuery.addEventListener('change', onChange)
    onScopeDispose(() => mediaQuery.removeEventListener('change', onChange))
  }

  const resolvedTheme = computed<'light' | 'dark'>(() =>
    preference.value === 'system' ? (systemDark.value ? 'dark' : 'light') : preference.value
  )

  watch(resolvedTheme, (theme) => {
    vuetify.theme.global.name.value = theme
    document.documentElement.style.colorScheme = theme
    const themeColor = document.querySelector('meta[name="theme-color"]')
    themeColor?.setAttribute('content', theme === 'dark' ? '#160F29' : '#6C3CF0')
  }, { immediate: true })

  function setPreference(p: ThemePreference) {
    preference.value = p
    writeStorage(PREFERENCE_KEY, p)
  }

  return { preference, resolvedTheme, setPreference }
})
